<template>
  <div class="dashboard">

    <v-alert type="info" v-html="$t('dashboard.infoText')" class="mb-4">
    </v-alert>

    <draggable
      v-model="widgets"
      :options="{
        animation: 100,
        handle: '.drag-widget'
      }"
      @end="updateIndex"
    >
      <transition-group name="fade" class="row">
        <template v-for="widget in availableWidgets">
          <component
            @closed="closeWidget"
            @resized="resizeWidget"
            :is="widget.type"
            v-if="widget.active"
            :key="widget.name"
            :size="widget.size"
            :processType="widget.processType"
          />
        </template>
      </transition-group>
    </draggable>

    <v-dialog v-model="dialog" width="400">
      <template v-slot:activator="{ on, attrs }">
        <v-fab-transition>
          <v-btn
            color="secondary"
            fixed
            bottom
            right
            fab
            v-bind="attrs"
            v-on="on"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-fab-transition>
      </template>

      <v-card>
        <v-card-title class="text-h5 lighten-2">
          {{ $t('dashboard.addWidget') }}
        </v-card-title>

        <v-divider></v-divider>

        <v-list nav dense>
          <v-list-item-group color="primary">
            <v-list-item
              v-for="widget in availableWidgets.filter((x) => !x.active)"
              :key="widget.name"
              @click="addWidget(widget)"
            >
              <v-list-item-icon>
                <v-icon>
                  {{ widget.icon }}
                </v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ widget.name }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
          <template v-if="!showAddWidget">
            <div class="muted text-center mb-4">
              {{ $t('dashboard.noWidgetsAvailable') }}
            </div>
          </template>
        </v-list>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapActions, mapGetters } from 'vuex';
import { feedbackEnums } from '@/enums/feedbacks.js';

// @ is an alias to /src
import todos from '@/components/widgets/Todos';
import calendar from '@/components/widgets/Calendar';
import processStats from '@/components/widgets/ProcessStatistics.vue';
import talkroundInfos from '@/components/widgets/TalkroundInfos.vue';
import supportInfos from '@/components/widgets/SupportInfos.vue';

export default {
  name: 'Dashboard',

  components: {
    todos,
    calendar,
    processStats,
    supportInfos,
    talkroundInfos,
    draggable
  },

  data() {
    return {
      dialog: false,
      widgets: [],
      standardWidgets: [
        {
          name: 'Termine',
          icon: 'mdi-calendar',
          type: 'calendar',
          active: true,
          initializeAsActive: false,
          index: 1,
          size: {
            sm: 12,
            md: 12
          }
        },
        {
          name: 'Aufgaben',
          icon: 'mdi-format-list-checks',
          type: 'todos',
          active: true,
          initializeAsActive: false,
          index: 2,
          size: {
            sm: 6,
            md: 4
          }
        },
        {
          name: 'MAFEG Monitor',
          icon: 'mdi-chart-bar',
          type: 'processStats',
          active: true,
          initializeAsActive: false,
          index: 3,
          size: {
            sm: 6,
            md: 4
          },
          processType: feedbackEnums.processTypes.MAFEG
        },
        {
          name: 'EHG Monitor',
          icon: 'mdi-chart-bar',
          type: 'processStats',
          active: true,
          initializeAsActive: false,
          index: 4,
          size: {
            sm: 6,
            md: 4
          },
          processType: feedbackEnums.processTypes.EHG
        },
        {
          name: 'PDC Monitor',
          icon: 'mdi-chart-bar',
          type: 'processStats',
          active: true,
          initializeAsActive: false,
          index: 5,
          size: {
            sm: 6,
            md: 4
          },
          processType: feedbackEnums.processTypes.PDC
        },
        {
          name: 'EDC Monitor',
          icon: 'mdi-chart-bar',
          type: 'processStats',
          active: true,
          initializeAsActive: false,
          index: 6,
          size: {
            sm: 6,
            md: 4
          },
          processType: feedbackEnums.processTypes.EDC
        },
        {
          name: 'PDP Monitor',
          icon: 'mdi-chart-bar',
          type: 'processStats',
          active: true,
          initializeAsActive: false,
          index: 7,
          size: {
            sm: 6,
            md: 4
          },
          processType: feedbackEnums.processTypes.PDP
        },
        {
          name: 'Support Infos',
          icon: 'mdi-question-mark',
          type: 'supportInfos',
          active: true,
          initializeAsActive: true,
          index: 9,
          size: {
            sm: 6,
            md: 4
          }
        }
      ]
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser'
    }),

    showAddWidget() {
      return this.availableWidgets.some((x) => !x.active);
    },

    availableWidgets() {
      if (!this.currentUser) {
        return [];
      }

      var widgets = [...this.widgets];
      widgets = widgets.filter(
        (x) =>
          x.type !== 'talkroundInfos' &&
          x.type !== 'supportInfos' &&
          (
            x.type !== 'processStats' ||
            this.currentUser.processTypesAsSupervisor.includes(x.processType)
          )
      );

      return widgets;
    }
  },

  async mounted() {
    const loadedWidgets = await this.loadWidgets();
    if (loadedWidgets && loadedWidgets.length > 0) {
      this.widgets = loadedWidgets;
      this.widgets.sort((x, y) => x.index - y.index);
      this.updateWidgets();
    } else {
      this.widgets = this.standardWidgets;
      this.saveWidgets(this.widgets);
    }
  },

  methods: {
    ...mapActions({
      refreshDashboard: 'dashboard/buildDashboardData',
      loadWidgets: 'dashboard/getWidgets',
      saveWidgets: 'dashboard/saveWidgets'
    }),

    async refresh() {
      await this.refreshDashboard();
    },

    closeWidget(widgetType) {
      const properties = Object.getOwnPropertyNames(widgetType);
      const widget = this.widgets.find((x) =>
        properties.every((p) => x[p] === widgetType[p])
      );
      widget.active = false;
      this.widgets
        .filter((x) => x.index > widget.index)
        .forEach((x) => {
          x.index -= 1;
        });
      widget.index = -1;
      this.saveWidgets(this.widgets);
    },

    addWidget(widget) {
      this.dialog = false;
      widget.active = true;
      widget.index = this.widgets.filter((x) => x.active).length;
      this.saveWidgets(this.widgets);
      this.widgets.sort((x, y) => x.index - y.index);
    },

    resizeWidget(data) {
      const widgetType = data.type;
      const properties = Object.getOwnPropertyNames(widgetType);
      const widget = this.widgets.find((x) =>
        properties.every((p) => x[p] === widgetType[p])
      );
      widget.size = data.size;
      this.saveWidgets(this.widgets);
    },

    updateIndex() {
      for (var i = 0; i < this.widgets.length; i++) {
        this.widgets[i].index = i + 1;
      }
      this.saveWidgets(this.widgets);
    },

    updateWidgets() {
      var save = false;
      this.widgets = this.widgets.filter((widget) => {
        const found = this.standardWidgets.find((x) =>
          this.compareWidgets(widget, x)
        );
        save ||= found === undefined;
        return found;
      });

      for (const widget of this.standardWidgets) {
        if (!this.widgets.find((x) => this.compareWidgets(widget, x))) {
          let widgetIndex = -1;
          if (widget.initializeAsActive) {
            widgetIndex = this.widgets.filter((x) => x.active).length;
          }

          widget.active = widget.initializeAsActive;
          widget.index = widgetIndex;
          this.widgets.push(widget);
          save = true;
        }
      }

      if (save) {
        this.saveWidgets(this.widgets);
      }
    },

    compareWidgets(a, b) {
      return a.name === b.name && a.type === b.type && a.icon === b.icon;
    }
  }
};
</script>

<style>
.drag-widget {
  cursor: move;
}
.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.fade-slow-enter-active,
.fade-slow-leave-active {
  transition: all 0.8s;
}

.fade-slow-enter,
.fade-slow-leave-active {
  opacity: 0;
}

.fade-fast-enter-active,
.fade-fast-leave-active {
  transition: all 0.2s;
}

.fade-fast-enter,
.fade-fast-leave-active {
  opacity: 0;
}

.dashboard .v-card.v-card--flat {
  height: 100%;
}
</style>
